import { useState } from "react";
import { CheckCircle } from "lucide-react";

function ContactForm({ t, lang, courseList }) {
  const [name, setName]       = useState("");
  const [phone, setPhone]     = useState("");
  const [course, setCourse]   = useState("");
  const [msg, setMsg]         = useState("");
  const [status, setStatus]   = useState("idle"); // idle | sending | success | error
  const [errors, setErrors]   = useState({});

  const validate = () => {
    const e = {};
    if (!name.trim())  e.name  = lang === "ru" ? "Введите имя" : lang === "en" ? "Enter your name" : "Adınızı yazın";
    if (!phone.trim()) e.phone = lang === "ru" ? "Введите номер" : lang === "en" ? "Enter phone number" : "Telefon yazın";
    if (!course)       e.course = lang === "ru" ? "Выберите курс" : lang === "en" ? "Select a course" : "Kurs seçin";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    setStatus("sending");

    try {
      const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id:  process.env.REACT_APP_EMAILJS_SERVICE,
          template_id: process.env.REACT_APP_EMAILJS_TEMPLATE,
          user_id:     process.env.REACT_APP_EMAILJS_KEY,
          template_params: {
            from_name: name,
            phone:     phone,
            course:    course,
            message:   msg || "—",
            reply_to: "codekloudacademy@gmail.com",
          },
        }),
      });
      if (res.ok) {
        setStatus("success");
        setName(""); setPhone(""); setCourse(""); setMsg("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-9 h-9 text-green-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t.contact.formSuccess}</h3>
        <button
          onClick={() => setStatus("idle")}
          className="text-blue-900 underline text-sm mt-2"
        >
          {lang === "ru" ? "Отправить ещё" : lang === "en" ? "Send another" : "Yenidən göndər"}
        </button>
      </div>
    );
  }

  const inputClass = (field) =>
    `w-full border rounded-xl px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-blue-900 focus:border-blue-900 ${errors[field] ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`;

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.contact.formTitle}</h3>
      <div className="space-y-4">

        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{t.contact.formNameLabel} <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); setErrors(v => ({...v, name: ""})); }}
            placeholder={t.contact.formNamePlaceholder}
            className={inputClass("name")}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{t.contact.formPhoneLabel} <span className="text-red-500">*</span></label>
          <input
            type="tel"
            value={phone}
            onChange={e => { setPhone(e.target.value); setErrors(v => ({...v, phone: ""})); }}
            placeholder={t.contact.formPhonePlaceholder}
            className={inputClass("phone")}
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>

        {/* Course */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{t.contact.formCourseLabel} <span className="text-red-500">*</span></label>
          <select
            value={course}
            onChange={e => { setCourse(e.target.value); setErrors(v => ({...v, course: ""})); }}
            className={inputClass("course")}
          >
            <option value="">{t.contact.formCoursePlaceholder}</option>
            {courseList.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {errors.course && <p className="text-red-500 text-xs mt-1">{errors.course}</p>}
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{t.contact.formMsgLabel}</label>
          <textarea
            value={msg}
            onChange={e => setMsg(e.target.value)}
            placeholder={t.contact.formMsgPlaceholder}
            rows={3}
            className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-blue-900 focus:border-blue-900 resize-none"
          />
        </div>

        {/* Error */}
        {status === "error" && (
          <p className="text-red-500 text-sm">{t.contact.formError}</p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={status === "sending"}
          className="w-full bg-blue-900 hover:bg-blue-800 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-all hover:scale-[1.02] active:scale-95 shadow-lg flex items-center justify-center gap-2"
        >
          {status === "sending" ? (
            <>
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              {t.contact.formSending}
            </>
          ) : (
            <>{t.contact.formSubmit}</>
          )}
        </button>

      </div>
    </div>
  );
}

export { ContactForm };
